/* Copyright (c) 2020-present CloudIO Inc */

import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  forwardRef,
} from 'react';
import { ItemID } from 'componentregistry';
import { makeStyles } from '@mui/styles';
import {
  useStateX,
  useStateXAtom,
  useStateXAtomValue,
  useStateXSelector,
} from '@cloudio-saas/statex';

import {
  itemIdPickerOpenAtom,
  itemIdPickerPathAtom,
} from 'layout/Sidebar/panels/TreeView/state';

import {
  Button,
  FormHelperText,
  Input,
  Grid,
  Typography,
  Theme,
} from '@mui/material';

import { PlatformContext } from 'platform/PlatformProvider';
import { DialogPopup } from 'components/Popup/DialogPopup';
import TreeView from 'layout/Sidebar/panels/TreeView/TreeViewPanel';
import TreeViewHeader from 'layout/Sidebar/panels/TreeView/TreeViewHeader';
import { activeItemSelector } from 'designer/activeItemSelector';
import { usePlatform } from 'platform/PlatformHooks';

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    '& input': {
      backgroundColor: theme.palette.background.paper,
      margin: '0 12px',
      lineHeight: '36px',
      padding: 0,
      paddingLeft: 8,
      height: 36,
    },
  },
  actionbtn: {},
  actions: {
    padding: 12,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    alignItems: 'center',
  },
  footer: { padding: 12, alignItems: 'center' },
  footerbtn: {},
}));

const HEIGHT = 600;
const WIDTH = 800;

type ItemIdUpdaterProps = {
  treename: string;
  pickedId: ItemID;
  onPickId: (value: ItemID) => void;
  showAlways?: boolean;
  btnLabel?: string;
  appUid: string;
  pageId: string;
};

type ItemIdPickerProps = {
  treename: string;
  pickedId: ItemID;
  onPickId: (value: ItemID) => void;
  appUid: string;
  pageId: string;
};

export function isUUID(itemId: string) {
  return (
    itemId.length > 25 &&
    (itemId.toUpperCase() === itemId || itemId.toLowerCase() === itemId)
  );
}

export const ItemIdUpdater = forwardRef(
  (
    {
      appUid,
      pickedId,
      pageId,
      onPickId,
      treename,
      showAlways,
      btnLabel,
    }: ItemIdUpdaterProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const platform = useContext(PlatformContext);
    const { itemId } = useStateXSelector(activeItemSelector, {
      params: { appUid, treename, pageId },
      props: platform,
    });
    const mgr = platform.getManager(itemId);
    const [value, setValue] = useState(itemId);
    const [error, setError] = useState('');
    const [shortened, setShortened] = useState(false);
    const classes = useStyles();
    const show = showAlways || isUUID(itemId);
    useEffect(() => {
      if (isUUID(itemId)) {
        setValue(platform.getShortenedId(appUid, pageId, itemId) ?? itemId);
        setShortened(true);
      } else {
        setValue(itemId);
      }
    }, [appUid, itemId, pageId, platform]);

    if (!show) {
      return null;
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      if (shortened) {
        setShortened(false);
      }
    };
    function onUpdate() {
      let err = '';
      if (itemId !== value) {
        if (value.length < 3) {
          err = 'Item ID must have atleast 3 chars!';
          setError(err);
          return err;
        }
        err = platform.updateItemID(appUid, pageId, itemId, value, true);
        setError(err);
        return err;
      }
    }

    const _onPickId = () => {
      const err = onUpdate();
      if (!err) {
        onPickId(value);
      }
    };
    const btnLabelToUse =
      btnLabel || (itemId !== value ? 'Update and Select' : 'Select');
    return (
      <div ref={ref}>
        <Grid container item xs className={classes.actions}>
          <Grid item xs={12}>
            <Typography>
              You selected an item with ID: <small>{itemId}</small>
            </Typography>
          </Grid>
          <Grid item xs="auto">
            {shortened ? 'We suggest' : 'Your choice'}
          </Grid>
          <Grid item xs>
            <Input
              spellCheck={false}
              autoCorrect="off"
              disabled={mgr?.isItemIdReadOnly()}
              className={classes.input}
              fullWidth
              type="text"
              value={value ?? ''}
              onChange={onChange}
              disableUnderline
            />
            {error ? <FormHelperText error>{error}</FormHelperText> : null}
          </Grid>

          <Grid item xs="auto">
            <Button
              onClick={_onPickId}
              className={classes.actionbtn}
              disabled={value === pickedId || mgr?.isItemIdReadOnly()}
              variant="contained"
              color="warning"
            >
              {btnLabelToUse}
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  },
);

export const ItemIdPicker = ({
  pickedId,
  onPickId,
  treename,
  pageId,
  appUid,
}: ItemIdPickerProps) => {
  const platform = usePlatform();
  const { itemId } = useStateXSelector(activeItemSelector, {
    params: { appUid, treename, pageId },
    props: platform,
  });
  const classes = useStyles();

  const btnLabel = 'Select';
  return (
    <div>
      <Grid container item xs className={classes.footer}>
        <Grid item xs>
          Selected Id : {itemId}
        </Grid>

        <Grid item xs="auto">
          <Button
            onClick={() => onPickId(itemId)}
            className={classes.footerbtn}
            disabled={itemId === pickedId}
            fullWidth
            variant="contained"
            color="primary"
          >
            {btnLabel}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

const TreePopup = ({ appUid, pageId }: { appUid: string; pageId: string }) => {
  const platform = usePlatform();
  const [open, setOpen] = useStateXAtom(itemIdPickerOpenAtom);
  const path = useStateXAtomValue(itemIdPickerPathAtom);
  const [pickedId, setPickedItemId] = useStateX(path, '');
  const ref = useRef<HTMLDivElement | null>(null);

  const handleClose = () => {
    setOpen(false);
  };

  const onPickId = (value: ItemID) => {
    setPickedItemId(value);
    handleClose();
  };
  if (!open) {
    return null;
  }

  return (
    <DialogPopup
      metadata
      platform={platform}
      appUid={appUid}
      bodyPadding={false}
      popupref={ref}
      popupHeight={HEIGHT}
      open={open}
      popupWidth={WIDTH}
      modal={false}
      pageId="$sidebar$"
      itemId="TreePopup"
      onClose={() => {
        handleClose();
      }}
      bodyChildren={
        <TreeView
          height={HEIGHT}
          treename="treepopup"
          noHeader
          platform={platform}
          appUid={appUid}
          pageUid={pageId}
        />
      }
      Header={
        <div style={{ marginRight: 32 }}>
          <TreeViewHeader
            treename="treepopup"
            platform={platform}
            appUid={appUid}
            pageId={pageId}
          />
        </div>
      }
      popupActions={
        <>
          <ItemIdUpdater
            appUid={appUid}
            pickedId={pickedId}
            onPickId={onPickId}
            treename="treepopup"
            pageId={pageId}
          />
          <ItemIdPicker
            appUid={appUid}
            pickedId={pickedId}
            onPickId={onPickId}
            treename="treepopup"
            pageId={pageId}
          />
        </>
      }
    />
  );
};

export default TreePopup;
